import { Theme } from "@material-ui/core/styles/createMuiTheme";

/** AppBar / Toolbar height: `spacing.unit × 6` (48px when unit = 8). */
export const appBarHeight = (theme: Theme) => theme.spacing.unit * 6;

/** Main content clearance below fixed AppBar: `spacing.unit × 8` (64px when unit = 8). */
export const appContainerHeight = (theme: Theme) => theme.spacing.unit * 8;

/** Base root styles for pages under the fixed AppBar. */
export const appPageRootStyles = (theme: Theme) => ({
  width: "100%",
  paddingTop: appContainerHeight(theme),
  boxSizing: "border-box" as const,
});

/** Full-height scrollable page root (table / list pages). */
export const appPageRootFullHeight = (theme: Theme) => ({
  ...appPageRootStyles(theme),
  height: `calc(100vh - ${theme.spacing.unit * 2}px)`,
  flexWrap: "nowrap" as const,
});

/** Min-height page root (forms, settings, notifications). */
export const appPageRootMinHeight = (theme: Theme) => ({
  ...appPageRootStyles(theme),
  minHeight: `calc(100vh - ${theme.spacing.unit * 2}px)`,
});

export const appBarUserRoleLabels: Record<string, string> = {
  ROLE_ADMIN: "Administrateur",
  ROLE_PROJECT_ADMIN: "Admin projet",
  ROLE_LABELER: "Labeliseur",
};

export const appBarNavChipStyle = {
  display: "inline-flex" as const,
  alignItems: "center" as const,
  gap: "6px",
  padding: "4px 10px",
  borderRadius: 8,
  fontSize: "0.8125rem",
  fontWeight: 500,
  lineHeight: 1.2,
  textTransform: "none" as const,
  textDecoration: "none" as const,
  color: "rgba(255, 255, 255, 0.88)",
  border: "1px solid transparent",
  transition: "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
};

export const appBarStyles = (theme: Theme) => {
  const barHeight = appBarHeight(theme);

  return {
    appBar: {
      background: `linear-gradient(45deg, ${theme.palette.primary.main} 10%, ${theme.palette.primary.light})`,
      height: barHeight,
      minHeight: barHeight,
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: 0,
      height: barHeight,
      minHeight: barHeight,
      padding: "0 16px",
      boxSizing: "border-box" as const,
    },
    logoLink: {
      display: "inline-flex",
      alignItems: "center",
      flexShrink: 0,
      textDecoration: "none",
      color: "#fff",
    },
    logoText: {
      fontSize: "1.0625rem",
      fontWeight: 600,
      lineHeight: 1,
      letterSpacing: "0.01em",
    },
    navAfterLogo: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      marginLeft: theme.spacing.unit * 20,
      minWidth: 0,
      flexWrap: "wrap" as const,
    },
    userSlot: {
      display: "inline-flex",
      alignItems: "center",
      flexShrink: 0,
      marginLeft: "auto",
      maxHeight: barHeight,
    },
    navChip: {
      ...appBarNavChipStyle,
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.12)",
        color: "#fff",
      },
    },
    navChipActive: {
      backgroundColor: "rgba(255, 255, 255, 0.22)",
      borderColor: "rgba(255, 255, 255, 0.45)",
      color: "#fff",
    },
    navChipIcon: {
      fontSize: 18,
      display: "flex",
      alignItems: "center",
    },
    userActionButton: {
      color: "inherit",
      textTransform: "none" as const,
      fontSize: "0.8125rem",
      fontWeight: 500,
      padding: "4px 8px",
      minWidth: "auto",
    },
    profileButton: {
      borderRadius: 8,
      padding: "4px 8px",
      maxHeight: 40,
    },
    profileIcon: {
      fontSize: 22,
    },
    profileTextBlock: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "flex-start",
      marginLeft: 6,
      lineHeight: 1.2,
      textAlign: "left" as const,
    },
    profileName: {
      color: "inherit",
      fontWeight: 500,
      fontSize: "0.8125rem",
      lineHeight: 1.2,
    },
    profileRole: {
      color: "rgba(255, 255, 255, 0.75)",
      fontSize: "0.6875rem",
      lineHeight: 1.2,
      fontWeight: 400,
      textTransform: "lowercase" as const,
    },
  };
};
